import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { EditSalesFunnelContext } from '@app/providers/EditSalesFunnelProvider/EditSalesFunnelContext';
import type { NEditSalesFunnelProvider } from '@app/providers/EditSalesFunnelProvider/EditSalesFunnelProvider.types';
import { NotificationContext } from '@app/providers/NotificationProvider/index';
import { handleCatchError } from '@app/services';
import { useGetSalesFunnelStagesWithRobotsQuery } from '@app/services/rtkQuery/SalesFunnelsStages/api';
import type { NFunnels } from '@app/types';

const Context = React.createContext('');

export const EditSalesFunnelProvider: NEditSalesFunnelProvider.Main = ({
  children,
}) => {
  const [query] = useSearchParams();
  const salesFunnelId = query.get('id');
  const {
    userAccessRights: {
      SalesFunnels: { IsOn },
    },
  } = useNewUserAccessRights();
  const [currentSalesFunnelStages, setCurrentSalesFunnelStages] = useState<
    NFunnels.SalesFunnelStageListDto[]
  >([]);

  const { activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const {
    data: currentSalesFunnelStagesData,
    error: salesFunnelStagesError,
    isLoading: isSalesFunnelStagesLoading,
    refetch: refetchGetSalesFunnelStages,
    isUninitialized: isGetSalesFunnelStagesWithRobotsUninitialized,
  } = useGetSalesFunnelStagesWithRobotsQuery(
    {
      salesFunnelId: salesFunnelId,
      ActiveBranchId: activeBranchId,
    },
    {
      skip: !activeBranchId || !salesFunnelId || !IsOn,
    }
  );

  useEffect(() => {
    if (!isGetSalesFunnelStagesWithRobotsUninitialized) {
      refetchGetSalesFunnelStages();
    }
  }, [
    isGetSalesFunnelStagesWithRobotsUninitialized,
    refetchGetSalesFunnelStages,
  ]);

  useEffect(() => {
    if (currentSalesFunnelStagesData) {
      setCurrentSalesFunnelStages(currentSalesFunnelStagesData);
    }
  }, [currentSalesFunnelStagesData, setCurrentSalesFunnelStages]);

  useEffect(() => {
    if (salesFunnelStagesError) {
      showNotification(handleCatchError(salesFunnelStagesError));
    }
  }, [showNotification, salesFunnelStagesError]);

  return (
    <EditSalesFunnelContext.Provider
      value={{
        currentSalesFunnelStages,
        isSalesFunnelStagesLoading,
        salesFunnelId,
        refetchGetSalesFunnelStages,
        salesFunnelStagesError,
      }}
    >
      <Context.Provider value={''}>{children}</Context.Provider>
    </EditSalesFunnelContext.Provider>
  );
};
